import React, { useState } from 'react';
import EmpireCommandCenter from './components/EmpireCommandCenter';
import EmpireCRM from './components/EmpireCRM';
import KYCForm from './components/KYCForm';
import EmpireReceipt from './components/EmpireReceipt';

/**
 * 🛰️ SAHELGUARD SYSTEM CORE - V7.0 (FULL INTEGRATION)
 * Gestionnaire de flux central : FINANCE -> CRM -> KYC -> REÇU
 * Architecture : Yarmotek | Sécurité : Sg-Alpha
 * Statut : SCELLÉ & DÉFINITIF
 * Database ID: ff988dfb-d854-4f4d-b158-10b2eab9bd18
 */

// 📡 CONFIGURATION DU PONT API (SCELLÉ)
// On utilise l'URL de ton Worker déployé
const API_URL = "https://sahelguard-api.sahelguard.workers.dev";

function App() {
  // Navigation interne : 'dashboard' | 'crm' | 'kyc' | 'receipt'
  const [currentScreen, setCurrentScreen] = useState('dashboard');
  
  // Mémoire temporaire pour le flux en cours (Transfert KYC vers Reçu)
  // Contient : Données Client, IMEI, Type de Plan, et ID de Transaction D1
  const [currentSessionData, setCurrentSessionData] = useState(null);

  /**
   * 1. Déclencheur après validation KYC
   * Intervient une fois que le Worker a validé l'écriture dans D1 et déduit le Wallet.
   */
  const handleKYCSuccess = (data) => {
    console.log("💰 Flux KYC Scellé sous protocole Yarmotek (Art. 8):", data);
    setCurrentSessionData(data);
    setCurrentScreen('receipt'); // Bascule automatique vers le reçu pour impression/preuve
  };

  /**
   * 2. Clôture de session après impression ou visualisation du reçu
   * Réinitialise le flux pour permettre une nouvelle opération.
   */
  const handleReceiptFinish = () => {
    setCurrentScreen('dashboard'); // Retour au centre de commandement financier (Empire Center)
    setCurrentSessionData(null);
  };

  /**
   * 3. Fonction de redirection rapide (Utilisée depuis le Dashboard)
   */
  const startNewSealing = () => {
    setCurrentScreen('kyc');
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-emerald-500 selection:text-black overflow-x-hidden">
      
      {/* 🧭 NAVIGATION BAR (ADMIN & AGENTS) - Masquée à l'impression via 'no-print' */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-[3000] flex items-center gap-2 bg-black/60 backdrop-blur-2xl p-1.5 rounded-full border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] no-print">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className={`px-6 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${currentScreen === 'dashboard' ? 'bg-white text-black shadow-lg scale-105' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
        >
          Empire Center
        </button>
        
        <button 
          onClick={() => setCurrentScreen('crm')}
          className={`px-6 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${currentScreen === 'crm' ? 'bg-white text-black shadow-lg scale-105' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
        >
          Empire CRM
        </button>

        {/* Séparateur visuel technique */}
        <div className="w-[1px] h-4 bg-white/10 mx-1" />

        <button 
          onClick={() => setCurrentScreen('kyc')}
          className={`px-6 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${currentScreen === 'kyc' ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/20 scale-105' : 'text-emerald-500/60 hover:text-emerald-500 hover:bg-emerald-500/5'}`}
        >
          Nouveau KYC
        </button>
      </nav>

      {/* 📱 ROUTAGE DYNAMIQUE DES COMPOSANTS (VUE PRINCIPALE) */}
      <main className="relative z-10">
        
        {/* Vue 1: Finance, Wallet & Ventilation (Logique 80% / 15% / 5%) */}
        {currentScreen === 'dashboard' && (
          <div className="animate-in fade-in duration-700">
            {/* On injecte l'apiUrl pour que le composant puisse fetcher la D1 */}
            <EmpireCommandCenter 
              onStartSealing={startNewSealing} 
              apiUrl={API_URL} 
            />
          </div>
        )}
        
        {/* Vue 2: Gestion Clients, IMEI & Alertes Assurance Raynal */}
        {currentScreen === 'crm' && (
          <div className="animate-in slide-in-from-left-4 duration-500">
            <EmpireCRM apiUrl={API_URL} />
          </div>
        )}
        
        {/* Vue 3: Formulaire d'inscription & Collecte des données KYC */}
        {currentScreen === 'kyc' && (
          <div className="pt-32 pb-12 min-h-screen flex items-center justify-center animate-in fade-in zoom-in duration-500 px-4">
            <div className="w-full max-w-4xl">
              <KYCForm onSubmitSuccess={handleKYCSuccess} apiUrl={API_URL} />
            </div>
          </div>
        )}

        {/* Vue 4: Génération de Reçu Scellé D1-BANK & Option Impression */}
        {currentScreen === 'receipt' && currentSessionData && (
          <div className="pt-24 pb-12 min-h-screen flex items-center justify-center animate-in slide-in-from-bottom duration-700 px-4">
            <EmpireReceipt 
              data={currentSessionData} 
              onFinish={handleReceiptFinish} 
            />
          </div>
        )}
      </main>

      {/* 🛡️ SECURITY WATERMARK - Indestructible & Masqué à l'impression */}
      <footer className="fixed bottom-6 right-8 text-[8px] font-black text-white/10 uppercase tracking-[0.5em] pointer-events-none no-print select-none z-0">
        SahelGuard OS v7.0 // Secure Terminal // Yarmotek Cloud // ID: ff988dfb-d854-4f4d-b158-10b2eab9bd18
      </footer>

      {/* 🌌 GLOBAL BACKGROUND EFFECTS (Optimisés pour performance) */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_top_right,_#10b98108,_transparent_50%)] pointer-events-none" />
      <div className="fixed inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] pointer-events-none" />
      <div className="fixed inset-0 bg-black -z-10" />
    </div>
  );
}

export default App;